import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import {
  type NumberingElement,
  type NumberingScheme,
  numberingSchemeApi,
} from '@/api/numbering-scheme.api';
import { numberingVocabApi } from '@/api/numbering-vocab.api';
import { organizationsApi } from '@/api/organizations.api';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { applyBrandColor } from '@/lib/brand';
import { useAuthStore } from '@/stores/auth.store';

const PRESET_COLORS = [
  '#0F2A47', '#1E40AF', '#0F766E', '#15803D',
  '#B91C1C', '#7C3AED', '#1F2937', '#0369A1',
];

const DEFAULT_FORMAT = '{org}-{type}-{dept}-{form}-{yy}{seq:4}-R{rev}';

// Compile visual builder elements into the engine format string (mirrors the
// backend). Kept in sync with numbering-scheme.service.ts compileElements().
const LIST_SLOTS = ['{type}', '{dept}', '{form}'];
// The generator has exactly these three per-document slots, so a list/free-text
// block beyond the third contributes nothing to the produced number.
const MAX_NAMED_ELEMENTS = LIST_SLOTS.length;

function compileElements(elements: NumberingElement[]): string {
  let listIdx = 0;
  return elements
    .map((el) => {
      if (el.kind === 'code') {
        return (el.shortName || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      }
      if (el.kind === 'list' || el.kind === 'input') {
        const ph = LIST_SLOTS[listIdx] ?? '';
        listIdx += 1;
        return ph;
      }
      const digits = Math.min(Math.max(Math.round(el.digits) || 4, 1), 10);
      return (el.yearPrefix ? '{yy}' : '') + `{seq:${digits}}`;
    })
    .filter(Boolean)
    .join('-');
}

type TFn = ReturnType<typeof useTranslation>['t'];

// A short human-readable chip for the "number layout" strip that previews the
// structure without exposing any {placeholder} syntax.
function blockChipText(el: NumberingElement, t: TFn): string {
  if (el.kind === 'code') return (el.shortName || '').toUpperCase() || 'ABC';
  if (el.kind === 'list') return el.name || t('admin.numbering_element_list');
  if (el.kind === 'input') return el.name || el.placeholder || t('admin.numbering_element_input');
  const digits = Math.min(Math.max(Math.round(el.digits) || 4, 1), 10);
  return (el.yearPrefix ? 'YY' : '') + '0'.repeat(Math.max(digits - 1, 0)) + '1';
}

function blockChipClass(el: NumberingElement): string {
  if (el.kind === 'list') return 'bg-primary-50 text-primary border border-primary/30';
  if (el.kind === 'input') return 'bg-info-bg text-info-fg border border-info border-dashed';
  if (el.kind === 'sequence') return 'bg-success-bg text-success';
  return 'bg-gray-100 text-gray-700';
}

export function AdminOrganizationPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'owner' || user?.role === 'admin';

  const { data: org, isLoading } = useQuery({
    queryKey: ['organizations', 'me'],
    queryFn: () => organizationsApi.me(),
  });

  const [name, setName] = useState('');
  const [brandColor, setBrandColor] = useState('#0F2A47');
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    if (org) {
      setName(org.name);
      setBrandColor(org.brandColor);
      setLogoUrl(org.logoUrl ?? '');
    }
  }, [org]);

  const save = useMutation({
    mutationFn: () =>
      organizationsApi.update({
        name: name.trim() || undefined,
        brandColor,
        logoUrl: logoUrl.trim() || null,
      }),
    onSuccess: (next) => {
      toast.success(t('admin.org_saved'));
      qc.setQueryData(['organizations', 'me'], next);
      applyBrandColor(next.brandColor);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AdminLayout title={t('admin.org_title')} subtitle={t('admin.org_subtitle')}>
      {isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : !org ? null : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            <div className="card space-y-5">
              <div>
                <label className="label">{t('admin.org_name')}</label>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  maxLength={200}
                />
              </div>

              <div>
                <label className="label">{t('admin.brand_color')}</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="color"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    disabled={!canEdit}
                    className="h-10 w-16 rounded cursor-pointer border border-border"
                  />
                  <input
                    className="input max-w-[120px]"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    disabled={!canEdit}
                    maxLength={7}
                  />
                  <div className="flex gap-1.5 flex-wrap">
                    {PRESET_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => canEdit && setBrandColor(c)}
                        disabled={!canEdit}
                        className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                        style={{ background: c, outline: brandColor === c ? `2px solid ${c}` : 'none', outlineOffset: 1 }}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="label">{t('admin.logo_url')}</label>
                <input
                  className="input"
                  placeholder="https://..."
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  disabled={!canEdit}
                  maxLength={500}
                />
              </div>

              {canEdit && (
                <div className="pt-3 border-t border-border flex justify-end">
                  <button
                    className="btn btn-primary"
                    onClick={() => save.mutate()}
                    disabled={save.isPending}
                  >
                    {save.isPending ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              )}
            </div>

            <aside>
              <div className="card !p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-border text-[11px] uppercase tracking-wide font-semibold text-muted">
                  {t('admin.preview')}
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-2.5">
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="h-8 w-8 rounded-md object-cover" />
                    ) : (
                      <div
                        className="h-8 w-8 rounded-md text-white flex items-center justify-center text-[13px] font-bold"
                        style={{ background: brandColor }}
                      >
                        {(name[0] ?? 'E').toUpperCase()}
                      </div>
                    )}
                    <strong className="text-[14px] text-gray-900">{name || 'Your org'}</strong>
                  </div>
                  <button
                    type="button"
                    className="w-full text-white font-semibold rounded-md py-2 text-[13px]"
                    style={{ background: brandColor }}
                  >
                    Review &amp; sign
                  </button>
                </div>
              </div>
            </aside>
          </div>

          <NumberingSchemesManager canEdit={canEdit} />

          <div className="mt-6">
            <ConfidentialityManager canEdit={canEdit} />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

/**
 * Admin-managed confidentiality classifications. Entries created here populate
 * the confidentiality dropdown in the create/upload wizard (Customer
 * requirement). Reuses the numbering-vocab store under the 'confidentiality'
 * kind. Code = short tag, label = descriptive name shown to users.
 */
function ConfidentialityManager({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');

  const { data: vocab } = useQuery({
    queryKey: ['numbering-vocab'],
    queryFn: () => numberingVocabApi.list(),
  });
  const items = vocab?.confidentiality ?? [];

  const createMutation = useMutation({
    mutationFn: () =>
      numberingVocabApi.create({
        kind: 'confidentiality',
        code: code.trim().toUpperCase(),
        label: label.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['numbering-vocab'] });
      setCode('');
      setLabel('');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => numberingVocabApi.delete(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['numbering-vocab'] }),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-gray-900 m-0 mb-1">
        {t('admin.confidentiality_section')}
      </h3>
      <p className="text-[13px] text-muted mb-4">{t('admin.confidentiality_subtitle')}</p>

      {items.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-4 text-center text-[13px] text-muted mb-3">
          {t('admin.confidentiality_empty')}
        </div>
      ) : (
        <ul className="space-y-1 mb-3">
          {items.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-2 border border-border rounded-md px-3 py-1.5"
            >
              <code className="text-[12px] font-mono bg-gray-100 text-gray-800 px-1.5 rounded">
                {c.code}
              </code>
              <span className="text-[13px] text-gray-900 flex-1">{c.label || '—'}</span>
              {canEdit && (
                <button
                  type="button"
                  className="text-danger text-[16px] leading-none px-1"
                  onClick={() => deleteMutation.mutate(c.id)}
                  title={t('common.delete') as string}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="label !text-[10px]">{t('admin.confidentiality_code')}</label>
            <input
              className="input !py-1 text-[12px] w-32 font-mono uppercase"
              placeholder="CONF"
              value={code}
              maxLength={20}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="label !text-[10px]">{t('admin.confidentiality_label_field')}</label>
            <input
              className="input !py-1 text-[12px]"
              placeholder={t('admin.confidentiality_label_ph') as string}
              value={label}
              maxLength={100}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!code.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? '…' : `+ ${t('common.create')}`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Manage a company's document-numbering schemes. Each scheme is one "naming
 * type" the user can pick in the create/upload wizard; it carries its own
 * format string and its own independent sequence counter.
 */
// Detailed, in-product guide for admins building a numbering scheme. Opened
// from the "?" Help button next to the section header. All copy is i18n-driven.
function NumberingHelpModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const chip = (text: string, cls: string) => (
    <span className={`px-2 py-0.5 rounded text-[12px] font-mono whitespace-nowrap ${cls}`}>{text}</span>
  );
  const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div>
      <h4 className="text-[14px] font-semibold text-gray-900 mb-1">{title}</h4>
      <div className="text-[13px] text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.numbering_help_title')}</h2>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-[13px] text-gray-700 leading-relaxed">{t('admin.numbering_help_intro')}</p>

          <Section title={t('admin.numbering_help_blocks_title')}>
            <p className="mb-3">{t('admin.numbering_help_blocks_intro')}</p>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="pt-0.5 shrink-0">{chip('İS', 'bg-gray-100 text-gray-700')}</div>
                <div>
                  <div className="font-medium text-gray-900">{t('admin.numbering_help_block_code_title')}</div>
                  <div>{t('admin.numbering_help_block_code_desc')}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="pt-0.5 shrink-0">
                  {chip('HR ▾', 'bg-primary-50 text-primary border border-primary/30')}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{t('admin.numbering_help_block_list_title')}</div>
                  <div>{t('admin.numbering_help_block_list_desc')}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="pt-0.5 shrink-0">
                  {chip('FHN ⌨', 'bg-info-bg text-info-fg border border-info border-dashed')}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{t('admin.numbering_help_block_input_title')}</div>
                  <div>{t('admin.numbering_help_block_input_desc')}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="pt-0.5 shrink-0">{chip('260012', 'bg-success-bg text-success')}</div>
                <div>
                  <div className="font-medium text-gray-900">{t('admin.numbering_help_block_seq_title')}</div>
                  <div>{t('admin.numbering_help_block_seq_desc')}</div>
                </div>
              </div>
            </div>
          </Section>

          <Section title={t('admin.numbering_help_presets_title')}>
            {t('admin.numbering_help_presets_desc')}
          </Section>
          <Section title={t('admin.numbering_help_order_title')}>
            {t('admin.numbering_help_order_desc')}
          </Section>
          <Section title={t('admin.numbering_help_preview_title')}>
            {t('admin.numbering_help_preview_desc')}
          </Section>
          <Section title={t('admin.numbering_help_user_title')}>
            {t('admin.numbering_help_user_desc')}
          </Section>

          <Section title={t('admin.numbering_help_example_title')}>
            <div>{t('admin.numbering_help_example_desc')}</div>
            <div className="flex flex-wrap items-center gap-1 mt-2">
              {chip('İS', 'bg-gray-100 text-gray-700')}
              <span className="text-muted">-</span>
              {chip('HR', 'bg-primary-50 text-primary border border-primary/30')}
              <span className="text-muted">-</span>
              {chip('FRM', 'bg-primary-50 text-primary border border-primary/30')}
              <span className="text-muted">-</span>
              {chip('260012', 'bg-success-bg text-success')}
              <span className="text-gray-400 mx-1">=</span>
              <code className="text-[14px] font-semibold text-gray-900 font-mono">İS-HR-FRM-260012</code>
            </div>
          </Section>

          <Section title={t('admin.numbering_help_tips_title')}>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t('admin.numbering_help_tip_1')}</li>
              <li>{t('admin.numbering_help_tip_2')}</li>
              <li>{t('admin.numbering_help_tip_3')}</li>
            </ul>
          </Section>
        </div>

        <div className="p-4 border-t border-border flex justify-end sticky bottom-0 bg-white">
          <button className="btn btn-primary btn-sm" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumberingSchemesManager({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const { data: schemes = [], isLoading } = useQuery({
    queryKey: ['numbering-schemes'],
    queryFn: () => numberingSchemeApi.list(),
  });

  return (
    <div className="card">
      {showHelp && <NumberingHelpModal onClose={() => setShowHelp(false)} />}
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-900 m-0">
          {t('admin.numbering_section')}
        </h3>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="btn btn-secondary btn-sm inline-flex items-center gap-1"
            onClick={() => setShowHelp(true)}
          >
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-white text-[10px] font-bold">?</span>
            {t('admin.numbering_help_button')}
          </button>
          {canEdit && !adding && (
            <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
              + {t('admin.numbering_scheme_add')}
            </button>
          )}
        </div>
      </div>
      <p className="text-[13px] text-muted mb-4">{t('admin.numbering_schemes_subtitle')}</p>

      {adding && (
        <div className="mb-4">
          <SchemeEditor
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              qc.invalidateQueries({ queryKey: ['numbering-schemes'] });
            }}
          />
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-muted text-[13px]">{t('common.loading')}</div>
      ) : schemes.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center text-[13px] text-muted">
          {t('admin.numbering_scheme_empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {schemes.map((s) => (
            <SchemeRow key={s.id} scheme={s} canEdit={canEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

function SchemeRow({ scheme, canEdit }: { scheme: NumberingScheme; canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState('');

  useEffect(() => {
    let cancelled = false;
    numberingSchemeApi
      .preview({ format: scheme.format, type: 'INT', dept: 'GRC', form: 'FRM', seq: scheme.nextSeq })
      .then((res) => {
        if (!cancelled) setPreview(res.number);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scheme.format, scheme.nextSeq]);

  const setDefault = useMutation({
    mutationFn: () => numberingSchemeApi.update(scheme.id, { isDefault: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['numbering-schemes'] }),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => numberingSchemeApi.delete(scheme.id),
    onSuccess: () => {
      toast.success(t('admin.numbering_scheme_deleted'));
      qc.invalidateQueries({ queryKey: ['numbering-schemes'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  if (editing) {
    return (
      <SchemeEditor
        scheme={scheme}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          qc.invalidateQueries({ queryKey: ['numbering-schemes'] });
        }}
      />
    );
  }

  return (
    <div className="border border-border rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <strong className="text-[14px] text-gray-900">{scheme.name}</strong>
          {scheme.isDefault && (
            <span className="badge badge-info text-[10px]">
              {t('admin.numbering_scheme_default_badge')}
            </span>
          )}
        </div>
        {scheme.elements && scheme.elements.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {scheme.elements.map((el, i) => (
              <span key={i} className="inline-flex items-center">
                {i > 0 && <span className="text-muted mx-0.5 text-[11px]">-</span>}
                <span className={`px-1.5 py-0.5 rounded text-[11px] font-mono ${blockChipClass(el)}`}>
                  {blockChipText(el, t)}
                </span>
              </span>
            ))}
          </div>
        )}
        <div className="text-[12px] text-gray-700 mt-1">
          <span className="text-muted">{t('admin.numbering_preview')}: </span>
          <code className="text-gray-900 font-medium">{preview || '…'}</code>
          <span className="text-muted"> · {t('admin.numbering_scheme_next_seq')}: {scheme.nextSeq}</span>
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1.5 shrink-0">
          {!scheme.isDefault && (
            <button
              className="text-[12px] text-primary hover:underline"
              onClick={() => setDefault.mutate()}
              disabled={setDefault.isPending}
            >
              {t('admin.numbering_scheme_make_default')}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
            {t('common.edit')}
          </button>
          <button
            className="btn btn-sm text-danger hover:bg-danger-bg border border-transparent"
            onClick={() => {
              if (confirm(t('admin.numbering_scheme_delete_confirm', { name: scheme.name }))) {
                remove.mutate();
              }
            }}
            disabled={remove.isPending}
          >
            {t('common.delete')}
          </button>
        </div>
      )}
    </div>
  );
}

function SchemeEditor({
  scheme,
  onCancel,
  onSaved,
}: {
  scheme?: NumberingScheme;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = !!scheme;
  const [name, setName] = useState(scheme?.name ?? '');
  const [elements, setElements] = useState<NumberingElement[]>(() => scheme?.elements ?? []);
  const [nextSeq, setNextSeq] = useState<number>(scheme?.nextSeq ?? 1);
  const [isDefault, setIsDefault] = useState<boolean>(scheme?.isDefault ?? false);
  const [preview, setPreview] = useState('');

  // The element builder is the only editor (raw {…} syntax, "Developer mode"
  // toggle and quick-start presets were removed per patron request). Legacy
  // schemes that still carry a raw format but no elements keep that format.
  const effectiveFormat = compileElements(elements) || scheme?.format || DEFAULT_FORMAT;

  // Blocks the server would reject. Previously these were silently dropped on
  // save: the request returned 200, the toast said "saved", and the block was
  // simply gone — which is what "the scheme can't be updated" actually was.
  // Now they are flagged here and the Save button waits for them.
  const blockIssues = elements.map((el) => {
    if (el.kind === 'code' && !el.shortName.trim()) return 'code_empty' as const;
    if (el.kind === 'list' && !el.options.some((o) => o.code.trim())) return 'list_empty' as const;
    return null;
  });
  const namedCount = elements.filter((el) => el.kind === 'list' || el.kind === 'input').length;
  const tooMany = namedCount > MAX_NAMED_ELEMENTS;
  const issues: string[] = [];
  blockIssues.forEach((issue, i) => {
    if (issue === 'code_empty') issues.push(t('admin.numbering_issue_code_empty', { n: i + 1 }));
    if (issue === 'list_empty') issues.push(t('admin.numbering_issue_list_empty', { n: i + 1 }));
  });
  if (tooMany) {
    issues.push(
      t('admin.numbering_issue_too_many', {
        max: MAX_NAMED_ELEMENTS,
        extra: namedCount - MAX_NAMED_ELEMENTS,
      }),
    );
  }

  useEffect(() => {
    let cancelled = false;
    numberingSchemeApi
      .preview({ format: effectiveFormat, type: 'INT', dept: 'GRC', form: 'FRM', seq: nextSeq })
      .then((res) => {
        if (!cancelled) setPreview(res.number);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [effectiveFormat, nextSeq]);

  const save = useMutation({
    mutationFn: () => {
      const payload: {
        name: string;
        nextSeq: number;
        isDefault: boolean;
        elements?: NumberingElement[];
        format?: string;
      } = { name: name.trim(), nextSeq, isDefault };
      if (elements.length > 0) {
        payload.elements = elements;
      } else {
        // No elements built — preserve a legacy scheme's existing raw format.
        payload.format = scheme?.format || DEFAULT_FORMAT;
      }
      return isEdit
        ? numberingSchemeApi.update(scheme!.id, payload)
        : numberingSchemeApi.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? t('admin.numbering_scheme_updated') : t('admin.numbering_scheme_added'));
      onSaved();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const move = (i: number, dir: -1 | 1) =>
    setElements((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  return (
    <div className="border border-primary rounded-lg p-4 bg-primary-50/40 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">{t('admin.numbering_scheme_name')}</label>
          <input
            className="input"
            placeholder={t('admin.numbering_scheme_name_ph') as string}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoFocus
          />
        </div>
        <div>
          <label className="label">{t('admin.numbering_scheme_next_seq')}</label>
          <input
            type="number"
            className="input max-w-[160px]"
            value={nextSeq}
            onChange={(e) => setNextSeq(Number(e.target.value) || 1)}
            min={1}
          />
          <p className="help">{t('admin.numbering_next_seq_hint')}</p>
        </div>
      </div>

      <div>
        <label className="label">{t('admin.numbering_format_label')}</label>
        <div className="space-y-3">
          <p className="text-[12px] text-muted">{t('admin.numbering_builder_intro')}</p>

          {elements.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 text-[12px]">
              <span className="text-muted mr-1">{t('admin.numbering_blocks_preview')}:</span>
              {elements.map((el, i) => (
                <span key={i} className="inline-flex items-center">
                  {i > 0 && <span className="text-muted mx-0.5">-</span>}
                  <span className={`px-2 py-0.5 rounded font-mono ${blockChipClass(el)}`}>
                    {blockChipText(el, t)}
                  </span>
                </span>
              ))}
            </div>
          )}

          {elements.length === 0 && (
            <p className="text-[12px] text-muted">{t('admin.numbering_builder_empty')}</p>
          )}
          {elements.map((el, i) => (
            <ElementRow
              key={i}
              el={el}
              index={i}
              total={elements.length}
              issue={blockIssues[i]}
              onChange={(next) =>
                setElements((arr) => arr.map((x, j) => (j === i ? next : x)))
              }
              onRemove={() => setElements((arr) => arr.filter((_, j) => j !== i))}
              onMove={(dir) => move(i, dir)}
            />
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              setElements((arr) => [...arr, { kind: 'code', documentType: '', shortName: '' }])
            }
          >
            + {t('admin.numbering_add_element')}
          </button>

          {issues.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-[12px] text-danger list-disc pl-5">
              {issues.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {preview && (
        <div className="bg-gray-50 border border-border rounded-md p-3">
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wider mb-1">
            {t('admin.numbering_preview')}
          </div>
          <code className="text-[14px] font-medium text-gray-900">{preview}</code>
        </div>
      )}

      <label className="flex items-center gap-2 text-[13px] text-gray-900 cursor-pointer">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        {t('admin.numbering_scheme_default')}
      </label>

      <div className="flex justify-end gap-2 pt-1">
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => save.mutate()}
          disabled={!name.trim() || issues.length > 0 || save.isPending}
          title={issues.length > 0 ? (t('admin.numbering_fix_issues') as string) : undefined}
        >
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  );
}

// One row in the numbering-format builder: a document-type short code or a
// running sequence element. Chained together they compile into the format.
function ElementRow({
  el,
  index,
  total,
  issue,
  onChange,
  onRemove,
  onMove,
}: {
  el: NumberingElement;
  index: number;
  total: number;
  /** Set when this block would be rejected on save (see SchemeEditor). */
  issue?: 'code_empty' | 'list_empty' | null;
  onChange: (next: NumberingElement) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  const hint =
    el.kind === 'code'
      ? t('admin.numbering_block_code_hint')
      : el.kind === 'list'
      ? t('admin.numbering_block_list_hint')
      : el.kind === 'input'
      ? t('admin.numbering_block_input_hint')
      : t('admin.numbering_block_seq_hint');
  return (
    <div
      className={`flex flex-wrap items-end gap-2 border rounded-md p-2 bg-white ${
        issue ? 'border-danger' : 'border-border'
      }`}
    >
      <div>
        <label className="label !text-[10px]">{t('admin.numbering_element_kind')}</label>
        <select
          className="input !py-1 text-[12px]"
          value={el.kind}
          onChange={(e) => {
            const k = e.target.value;
            onChange(
              k === 'sequence'
                ? { kind: 'sequence', name: el.name, digits: 5, yearPrefix: false }
                : k === 'list'
                ? { kind: 'list', name: el.name, options: [] }
                : k === 'input'
                ? { kind: 'input', name: el.name, placeholder: '' }
                : { kind: 'code', name: el.name, documentType: '', shortName: '' },
            );
          }}
        >
          <option value="code">{t('admin.numbering_element_code')}</option>
          <option value="list">{t('admin.numbering_element_list')}</option>
          <option value="input">{t('admin.numbering_element_input')}</option>
          <option value="sequence">{t('admin.numbering_element_sequence')}</option>
        </select>
      </div>

      {el.kind === 'code' ? (
        <>
          <div>
            <label className="label !text-[10px]">{t('admin.numbering_element_doctype')}</label>
            <input
              className="input !py-1 text-[12px] w-32"
              placeholder="Form"
              value={el.documentType ?? ''}
              maxLength={60}
              onChange={(e) => onChange({ ...el, documentType: e.target.value })}
            />
          </div>
          <div>
            <label className="label !text-[10px]">{t('admin.numbering_element_shortname')}</label>
            <input
              className="input !py-1 text-[12px] w-24 font-mono uppercase"
              placeholder="FRM"
              value={el.shortName}
              maxLength={20}
              onChange={(e) => onChange({ ...el, shortName: e.target.value })}
            />
          </div>
        </>
      ) : el.kind === 'list' ? (
        <div className="flex-1 min-w-[240px] space-y-2">
          <div>
            <label className="label !text-[10px]">{t('admin.numbering_element_name')}</label>
            <input
              className="input !py-1 text-[12px]"
              placeholder="Company name"
              value={el.name ?? ''}
              maxLength={60}
              onChange={(e) => onChange({ ...el, name: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            {el.options.map((o, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input
                  className="input !py-1 text-[12px] flex-1"
                  placeholder={t('admin.numbering_list_option_label') as string}
                  value={o.label}
                  maxLength={100}
                  onChange={(e) =>
                    onChange({
                      ...el,
                      options: el.options.map((x, j) =>
                        j === oi ? { ...x, label: e.target.value } : x,
                      ),
                    })
                  }
                />
                <input
                  className="input !py-1 text-[12px] w-24 font-mono uppercase"
                  placeholder={t('admin.numbering_list_option_code') as string}
                  value={o.code}
                  maxLength={12}
                  onChange={(e) =>
                    onChange({
                      ...el,
                      options: el.options.map((x, j) =>
                        j === oi ? { ...x, code: e.target.value.toUpperCase() } : x,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  className="text-danger text-[15px] px-1 leading-none"
                  onClick={() =>
                    onChange({ ...el, options: el.options.filter((_, j) => j !== oi) })
                  }
                  title={t('common.remove') as string}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                onChange({ ...el, options: [...el.options, { label: '', code: '' }] })
              }
            >
              + {t('admin.numbering_list_add_option')}
            </button>
          </div>
        </div>
      ) : el.kind === 'input' ? (
        <div className="flex-1 min-w-[240px] flex flex-wrap gap-2">
          <div className="flex-1 min-w-[140px]">
            <label className="label !text-[10px]">{t('admin.numbering_element_name')}</label>
            <input
              className="input !py-1 text-[12px]"
              placeholder={t('admin.numbering_example_company') as string}
              value={el.name ?? ''}
              maxLength={60}
              onChange={(e) => onChange({ ...el, name: e.target.value })}
            />
          </div>
          <div className="w-28">
            <label className="label !text-[10px]">{t('admin.numbering_element_input_ph')}</label>
            <input
              className="input !py-1 text-[12px] font-mono uppercase"
              placeholder="FHN"
              value={el.placeholder ?? ''}
              maxLength={20}
              onChange={(e) => onChange({ ...el, placeholder: e.target.value.toUpperCase() })}
            />
          </div>
        </div>
      ) : (
        <>
          <div>
            <label className="label !text-[10px]">{t('admin.numbering_element_digits')}</label>
            <input
              type="number"
              min={1}
              max={10}
              className="input !py-1 text-[12px] w-20"
              value={el.digits}
              onChange={(e) => onChange({ ...el, digits: Number(e.target.value) || 5 })}
            />
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-gray-700 pb-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={el.yearPrefix}
              onChange={(e) => onChange({ ...el, yearPrefix: e.target.checked })}
            />
            {t('admin.numbering_element_yearprefix')}
          </label>
        </>
      )}

      <div className="flex items-center gap-0.5 ml-auto pb-0.5">
        <button
          type="button"
          className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-[14px] px-1 leading-none"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          title={t('admin.numbering_move_left') as string}
        >
          ◀
        </button>
        <button
          type="button"
          className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-[14px] px-1 leading-none"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          title={t('admin.numbering_move_right') as string}
        >
          ▶
        </button>
        <button
          type="button"
          className="text-danger text-[16px] px-2 leading-none"
          onClick={onRemove}
          title={t('common.remove') as string}
        >
          ×
        </button>
      </div>

      <p className="w-full text-[11px] text-muted mt-0.5">{hint}</p>
    </div>
  );
}
